import { Moment } from 'moment'

import { ConfigItem, DefaultFile, EmptyDefaultFile, SearchResult } from 'Core/interfaces'
import { ConfigFilterItem } from 'Core/interfaces'

import { ManifestServiceItem } from 'Portals/Internal/entities/SupplierInvoice'

export enum ManifestSearchSuggestions {
	manifestIds = 'manifestIds',
	manifestDocumentNumbers = 'manifestDocumentNumbers',
	serviceOccurrenceIds = 'serviceOccurrenceIds',
	serviceSiteIds = 'serviceSiteIds',
	serviceSiteDocumentNumbers = 'serviceSiteDocumentNumbers'
}

export enum ManifestFilterFields {
	startDate = 'startDate',
	endDate = 'endDate'
}

export enum TransporterSearchSuggestions {
	searchById = 'searchById',
	searchByName = 'searchByName',
	searchByDotId = 'searchByDotId',
	searchByEpaId = 'searchByEpaId',
	searchByHazHaulingClass = 'searchByHazHaulingClass',
	searchBySupplierLocationId = 'searchBySupplierLocationId'
}

export enum WastestreamSearchSuggestions {
	searchById = 'searchById',
	searchByName = 'searchByName'
}

export enum DisposalFacilitiesSearchResultFields {
	disposalFacilityNameSuggestions = 'disposalFacilityNameSuggestions',
	supplierLocationIdSuggestions = 'supplierLocationIdSuggestions',
	supplierLocationNameSuggestions = 'supplierLocationNameSuggestions'
}

export interface DisposalFacilitiesResult {
	[DisposalFacilitiesSearchResultFields.disposalFacilityNameSuggestions]: DisposalFacility[]
	[DisposalFacilitiesSearchResultFields.supplierLocationIdSuggestions]: DisposalFacility[]
	[DisposalFacilitiesSearchResultFields.supplierLocationNameSuggestions]: DisposalFacility[]
}

export interface WastestreamEntity extends Entity {
	managementMethod: Entity
}

export interface WastestreamManagementLabelValue extends LabelValue<number> {
	managementMethod: Entity
}

interface DisposalFacility extends Entity {
	epaId: string
	isDefault: true
	isActive: true
	supplierId: number
	supplierName: string
	supplierActive: true
	supplierLocationName: string
	supplierLocationId: number
	supplierLocationCity: string
	supplierLocationState: string
	supplierLocationActive: true
	stateRegId: string
}

interface Transporter extends Entity {
	dotId: string
	epaId: string
	isActive: boolean
	isApproved: boolean
	supplierLocation: SupplierLocation
}

export interface PaymentTerms {
	billingAgency: string
	paymentTerm: string
}

interface SupplierLocation {
	id: number
	vendorId: string
	vendorLocation: string
	remitAddress: RemitAddress
	supplier: Entity
	name: string
	nameInNativeLanguage: string
	corporateEntityType: Entity
	greatPlainsPaymentClassCode: string
	invoiceDifficultyLevelCode: string
	facilityTypes: Entity[]
	status: Entity
	orderDispatchWeb: boolean
	orderDispatchEmail: boolean
	orderDispatchManual: boolean
	statusComments: string
	isActive: boolean
	isCustomerIndemnified: boolean
	isMinoritySupplier: boolean
	isTax1099Supplier: boolean
	isAdminOnlySupplier: boolean
	isBillingAgentOnly: boolean
	isDataTrackingOnly: boolean
	expirationDate: string
	lastSiteVisitDate: string
	defaultGLAccount: string
	defaultAffiliateCode: string
	adminApprovalContact: Entity
	tax1099VendorType: Entity
	tax1099BoxNumber: Entity
	taxNumber: string
	legalName: string
	orderAcceptanceHours: number
	qualityRating: number
	schedFlexRating: number
	unitCostTolerancePercentage: number
	volumeTolerancePercentage: number
	serviceOccurrenceTolerancePercentage: number
	suplLocRefCode: string
	invoicePaymentComments: string
	comments: string
	w9Document: Entity
	eftDocument: Entity
	classCode: string
	paymentTerms: PaymentTerms[]
}

interface RemitAddress {
	name: string
	isVendorActive: true
	isRemitAddressActive: true
	addressLine: string
	city: string
	stateCode: string
	stateId: number
	zipCode: string
}

export declare namespace Manifest {
	interface Detailed {
		id: Key
		serviceSiteDocumentNumber: string
		manifestDocumentNumber: string
		isDiscrepancy: boolean
		serviceSiteId: number | null
		serviceSite: Entity | null
		serviceOccurrenceIds: number[]
		disposalFacilityId: number | null
		disposalFacility: DisposalFacility
		deliveryDate: string
		generatorShipmentDate: string
		designatedDisposalFacilityAcceptanceDate: string
		epaServiceSiteId: string
		epaDisposalFacilityId: string
		stateDisposalFacilityId: string
		stateServiceSiteId: string
		additionalDescriptionWasteCodes: string
		specialHandlingDescription: string
		additionalDescriptionMaterial: string
		discrepancyDescription: string
		manifestDocument: Entity | null
		biohazardousManifestDocument: Entity | null
		manifestItems: ManifestItem.Detailed[]
		manifestTransporters: ManifestTransporter.Detailed[]
	}

	namespace ManifestItem {
		interface Detailed extends Entity {
			serviceOccurrenceId: number
			wastestream: WastestreamEntity
			manifestId: number
			pageNumber: number | null
			lineNumberAndLetter: string | null
			containerUnitOfMeasureCode: string
			containerQuantity: number
			totalQuantity: number
			weightUnitOfMeasureCode: string
			weight: number
			managementMethod: Entity | null
			dotDescription: string | null
			transactionItemIds: number[]
			manifestItemWasteCodes: WasteCode.Detailed[]
		}

		namespace Form {
			interface Generic {
				name: BasicString
				wastestreamId: null | LabelValue<number>
				pageNumber: number | null
				lineNumberAndLetter: BasicString
				containerUnitOfMeasureCode: BasicString
				containerQuantity: number | null
				totalQuantity: number | null
				weightUnitOfMeasureCode: BasicString
				weight: number | null
				managementMethodId: number | null
				dotDescription: BasicString
			}
			interface Add extends Generic {}
		}

		namespace Request {
			interface Generic {
				name: string | null
				wastestreamId: number | null
				pageNumber: number | null
				lineNumberAndLetter: string | null
				containerUnitOfMeasureCode: string | null
				containerQuantity: number | null
				totalQuantity: number | null
				weightUnitOfMeasureCode: string | null
				weight: number | null
				managementMethodId: number | null
				dotDescription: string | null
			}
			interface Add extends Generic {}
			interface Edit extends Generic {}
		}
	}

	namespace WasteCode {
		interface Detailed {
			id: number | null
			manifestItemId: number | null
			hazardCodeSequenceNumber: BasicString
			stateProvinceId: number | null
			stateProvince: Entity | null
			stateHazardousCode: CodeEntity | null
			epaHazardousCode: CodeEntity | null
		}

		interface Basic extends Detailed {
			dotDescription: ManifestItem.Detailed['dotDescription']
			pageNumber: ManifestItem.Detailed['pageNumber']
			lineNumberAndLetter: ManifestItem.Detailed['lineNumberAndLetter']
		}

		namespace Form {
			interface Generic {
				manifestItemId: BasicNumber
				hazardCodeSequenceNumber: BasicString
				stateProvince: LabelValue<number> | null
				stateHazardousCodeId: BasicNumber
				epaHazardousCodeId: BasicNumber
			}
		}

		namespace Request {
			interface Generic {
				manifestItemId: BasicNumber
				hazardCodeSequenceNumber: BasicString
				stateProvinceId: BasicNumber
				stateHazardousCodeId: BasicNumber
				epaHazardousCodeId: BasicNumber
			}
		}

		namespace Search {
			namespace StateHazardous {
				interface Result extends CodeEntity {
					stateProvince: Entity
				}
			}
		}
	}

	namespace ManifestService {
		namespace Form {
			interface Add
				extends Manifest.Form.Edit,
					Manifest.ManifestItem.Form.Add,
					Manifest.ManifestTransporter.Form.Generic {}
		}
		namespace Request {
			interface Add {
				manifest: Manifest.Request.Edit & {
					transporter: ManifestTransporter.Request.Generic
				}
				manifestItem: ManifestItem.Request.Add
			}
		}
		namespace Response {
			interface Add {
				manifest: Entity
				manifestItem: ManifestServiceItem
			}
		}
	}

	namespace ManifestTransporter {
		interface Detailed {
			id: number
			transporter: Transporter
			manifestId: number
			transporterOrderSequence: number
			stateTransporterId: string
			epaTransporterId: string
			createDate: string
		}

		namespace Form {
			interface Generic {
				transporterId: LabelValue<number> | null
				transporterOrderSequence: BasicString
				stateTransporterId: BasicString
				epaTransporterId: BasicString
			}
		}

		namespace Request {
			interface Generic extends Omit<Form.Generic, 'transporterId'> {
				transporterId: BasicNumber
			}
		}
	}

	namespace Search {
		interface Suggestions {
			[ManifestSearchSuggestions.manifestIds]: Entity[]
			[ManifestSearchSuggestions.manifestDocumentNumbers]: Entity['name'][]
			[ManifestSearchSuggestions.serviceOccurrenceIds]: Entity[]
			[ManifestSearchSuggestions.serviceSiteIds]: Entity[]
			[ManifestSearchSuggestions.serviceSiteDocumentNumbers]: Entity['name'][]
		}

		interface Result extends SearchResult {}

		interface Config {
			[ManifestSearchSuggestions.serviceSiteIds]: ConfigItem<ManifestSearchSuggestions>
			[ManifestSearchSuggestions.manifestIds]: ConfigItem<ManifestSearchSuggestions>
			[ManifestSearchSuggestions.manifestDocumentNumbers]: ConfigItem<ManifestSearchSuggestions>
			[ManifestSearchSuggestions.serviceOccurrenceIds]: ConfigItem<ManifestSearchSuggestions>
			[ManifestSearchSuggestions.serviceSiteDocumentNumbers]: ConfigItem<ManifestSearchSuggestions>
		}

		interface Transporter {
			[TransporterSearchSuggestions.searchById]: number[]
			[TransporterSearchSuggestions.searchByName]: Entity[]
			[TransporterSearchSuggestions.searchByDotId]: Entity[]
			[TransporterSearchSuggestions.searchByEpaId]: Entity[]
			[TransporterSearchSuggestions.searchByHazHaulingClass]: Entity[]
			[TransporterSearchSuggestions.searchBySupplierLocationId]: Entity[]
		}

		interface Wastestream {
			[WastestreamSearchSuggestions.searchById]: WastestreamEntity[]
			[WastestreamSearchSuggestions.searchByName]: WastestreamEntity[]
		}
	}

	namespace Filter {
		interface Config {
			[ManifestFilterFields.startDate]: ConfigFilterItem<string, null>
			[ManifestFilterFields.endDate]: ConfigFilterItem<string, null>
		}
	}

	namespace Form {
		interface Add
			extends Pick<
				Detailed,
				'serviceSiteDocumentNumber' | 'manifestDocumentNumber' | 'isDiscrepancy'
			> {
			disposalFacilityId: LabelValue<number> | null
			generatorShipmentDate: Moment | null
			deliveryDate: Moment | null
			designatedDisposalFacilityAcceptanceDate: Moment | null
		}

		interface Edit {
			serviceSiteDocumentNumber: BasicString
			manifestDocumentNumber: BasicString
			isDiscrepancy: boolean
			stateDisposalFacilityId: BasicString
			additionalDescriptionWasteCodes: BasicString
			specialHandlingDescription: BasicString
			discrepancyDescription: BasicString
			epaServiceSiteId: BasicString
			epaDisposalFacilityId: BasicString
			stateServiceSiteId: BasicString
			serviceSiteId: LabelValue<number> | null
			disposalFacilityId: LabelValue<number> | null
			additionalDescriptionMaterial: BasicString
			biohazardousManifestDocument: DefaultFile | EmptyDefaultFile | null
			manifestDocument: DefaultFile | EmptyDefaultFile | null
			deliveryDate: Moment | null
			designatedDisposalFacilityAcceptanceDate: Moment | null
			generatorShipmentDate: Moment | null
		}
	}

	namespace Request {
		interface Add {
			serviceSiteDocumentNumber: BasicString
			manifestDocumentNumber: BasicString
			isDiscrepancy: boolean
			disposalFacilityId: BasicNumber
			generatorShipmentDate: BasicString
			deliveryDate: BasicString
			designatedDisposalFacilityAcceptanceDate: BasicString
			manifestFile: {}
			biohazardousManifestFile: {}
		}

		interface Edit {
			serviceSiteDocumentNumber: BasicString
			manifestDocumentNumber: BasicString
			isDiscrepancy: boolean
			serviceSiteId: BasicNumber
			disposalFacilityId: BasicNumber
			generatorShipmentDate: BasicString
			deliveryDate: BasicString
			designatedDisposalFacilityAcceptanceDate: BasicString
			epaServiceSiteId: BasicString
			epaDisposalFacilityId: BasicString
			stateDisposalFacilityId: BasicString
			stateServiceSiteId: BasicString
			additionalDescriptionWasteCodes: BasicString
			specialHandlingDescription: BasicString
			discrepancyDescription: BasicString
			additionalDescriptionMaterial: BasicString
			'BiohazardousManifestFile.HasValue': boolean
			'BiohazardousManifestFile.Value': File | null
			'ManifestFile.Value': File | null
			'ManifestFile.HasValue': boolean
		}
	}
}
